import React from 'react';
import { usePathname } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { AIActionKey, FeatureKey, MonetizationPlacement, PaidPlanTier, PlanSet } from '@tabbio/types';
import { useEntitlement } from '../../providers/SubscriptionProvider.shared';
import { useUpgradeModal } from '../../providers/UpgradeModalProvider';
import { TabbioAIIcon } from '../common/TabbioAIIcon';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { WebPageHeader } from '../common/WebPageHeader';
import { createUpgradeContext } from '../../lib/monetization-context';

interface Props {
  featureKey: FeatureKey | AIActionKey;
  children: React.ReactNode;
  /** Shown above the upgrade card when feature is locked. */
  lockedTitle?: string;
  lockedSubtitle?: string;
  onBack?: () => void;
  /** For top-level/tab routes: hide back button on mobile (drawer handles nav). */
  hideBackOnMobile?: boolean;
  /** Feature-specific hero (icon / headline / description) for the locked card.
      Falls back to the generic localized Pro/Plus copy: NEVER the raw backend
      decision message. */
  heroIcon?: React.ReactNode;
  heroTitle?: string;
  heroSubtitle?: string;
  /** Feature-specific benefit rows; falls back to the generic plan benefits. */
  benefits?: { title: string; body: string }[];
}

function getCtaKey(planSet: PlanSet, tier: PaidPlanTier) {
  if (planSet === 'employer') {
    return tier === 'pro'
      ? 'entitlementGate.ctaUpgradeEmployerPro'
      : 'entitlementGate.ctaUpgradeEmployerPlus';
  }
  return tier === 'pro'
    ? 'entitlementGate.ctaUpgradeIndividualPro'
    : 'entitlementGate.ctaUpgradeIndividualPlus';
}

function getBenefitKeys(featureKey: FeatureKey | AIActionKey, planSet: PlanSet) {
  const key = String(featureKey);
  if (planSet === 'employer' || key.startsWith('employer.')) {
    return ['employerReach', 'employerRanking', 'employerWorkflow'] as const;
  }
  return ['careerArtifacts', 'applicationPack', 'marketPrep'] as const;
}

function getPlacement(featureKey: FeatureKey | AIActionKey): MonetizationPlacement {
  const key = String(featureKey);
  if (key.includes('download')) return 'export';
  if (key.includes('analytics')) return 'analytics';
  if (key.includes('branding') || key.includes('domain')) return 'branding';
  if (key.includes('connector')) return 'connectors';
  if (key.includes('automation') || key.includes('nova')) return 'nova_activate';
  return 'generic';
}

export function EntitlementGate({
  featureKey,
  children,
  lockedTitle,
  lockedSubtitle,
  onBack,
  hideBackOnMobile,
  heroIcon,
  heroTitle,
  heroSubtitle,
  benefits,
}: Props) {
  const decision = useEntitlement(featureKey);
  const upgradeModal = useUpgradeModal();
  const { t } = useTranslation('common');
  const pathname = usePathname();

  if (decision.reasonCode === 'loading') {
    return (
      <div
        style={{
          flex: 1,
          backgroundColor: 'var(--bg)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-tertiary)',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          fontSize: 14,
        }}
      >
        {t('entitlementGate.loading')}
      </div>
    );
  }

  if (decision.allowed) {
    return <>{children}</>;
  }

  const recommendedTier: PaidPlanTier = decision.recommendedTier ?? 'plus';
  const recommendedPlanSet: PlanSet = decision.recommendedPlanSet ?? 'individual';
  const eyebrowKey =
    recommendedTier === 'pro'
      ? 'entitlementGate.eyebrowPro'
      : 'entitlementGate.eyebrowPlus';
  const titleKey =
    recommendedTier === 'pro'
      ? 'entitlementGate.titlePro'
      : 'entitlementGate.titlePlus';
  const descriptionKey =
    recommendedTier === 'pro'
      ? 'entitlementGate.descriptionPro'
      : 'entitlementGate.descriptionPlus';
  const ctaKey = getCtaKey(recommendedPlanSet, recommendedTier);
  const benefitKeys = getBenefitKeys(featureKey, recommendedPlanSet);

  const standalone = Boolean(lockedTitle || onBack);

  const body = (
    <>
      {standalone && (
        <WebPageHeader
          title={lockedTitle ?? ''}
          subtitle={lockedSubtitle}
          onBack={onBack}
          hideBackOnMobile={hideBackOnMobile}
        />
      )}
      {/* Hero card: Gemini suggestion-card pattern (§7.4.1):
          var(--menu-bg) surface + var(--gem-input-shadow), no border. */}
      <section
          style={{
            borderRadius: 28,
            background: 'var(--menu-bg)',
            boxShadow: 'var(--gem-input-shadow)',
            padding: '28px 28px 32px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 14,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {heroIcon ?? <TabbioAIIcon size={30} />}
            </div>
            <span className="tabbio-menu-badge" style={{ flexShrink: 0 }}>
              {t(eyebrowKey)}
            </span>
          </div>

          {/* Designed copy ONLY: the raw backend decision message ("Automation
              (Nova) requires pro or higher.") must never be the headline. */}
          <h1
            style={{
              margin: 0,
              fontFamily: "'Google Sans', 'Google Sans Text', sans-serif",
              fontSize: 32,
              lineHeight: 1.2,
              fontWeight: 400,
              color: 'var(--text-primary)',
              letterSpacing: '-0.3px',
            }}
          >
            {heroTitle ?? t(titleKey)}
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.5,
              color: 'var(--text-secondary)',
              maxWidth: 620,
            }}
          >
            {heroSubtitle ?? t(descriptionKey)}
          </p>

          <button
            type="button"
            onClick={() =>
              upgradeModal.open(
                createUpgradeContext({
                  decision,
                  placement: getPlacement(featureKey),
                  entryPoint: `entitlement_gate.${String(featureKey)}`,
                  returnTo: pathname,
                }),
              )
            }
            style={{
              marginTop: 8,
              alignSelf: 'flex-start',
              minHeight: 44,
              padding: '0 20px',
              borderRadius: 9999,
              border: 0,
              backgroundColor: 'var(--text-primary)',
              color: 'var(--bg)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'opacity 150ms',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.88';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <span>{t(ctaKey)}</span>
            <MaterialSymbol name="arrow_forward" size={18} />
          </button>
        </section>

        {/* Benefits */}
        <section style={{ marginTop: 40 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 500,
              color: 'var(--text-primary)',
              padding: '0 8px',
              marginBottom: 12,
            }}
          >
            {t('entitlementGate.resourcesTitle')}
          </div>
          <div
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: 'var(--text-tertiary)',
              padding: '0 8px',
              marginBottom: 16,
            }}
          >
            {t('entitlementGate.resourcesSubtitle')}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(
              benefits ??
              benefitKeys.map((itemKey) => ({
                title: t(`entitlementGate.benefits.${itemKey}.title`),
                body: t(`entitlementGate.benefits.${itemKey}.body`),
              }))
            ).map((item) => (
              <div key={item.title} className="tabbio-list-row">
                <span className="tabbio-list-row-icon" aria-hidden>
                  <MaterialSymbol name="check_circle" size={22} />
                </span>
                <div className="tabbio-list-row-body">
                  <div className="tabbio-list-row-title">{item.title}</div>
                  <div className="tabbio-list-row-sub" style={{ whiteSpace: 'normal' }}>
                    {item.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
    </>
  );

  if (!standalone) return body;

  return (
    <div
      style={{
        flex: 1,
        backgroundColor: 'var(--bg)',
        minHeight: '100vh',
        overflowY: 'auto',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        color: 'var(--text-primary)',
      }}
    >
      <div className="tabbio-svc-page">{body}</div>
    </div>
  );
}
